import type { TocBlockAbstract } from '@lighthouse/core'
import type { TocHeading } from '@lighthouse/shared'
import { getCursorStyle, useApplicationContext } from '@lighthouse/shared'
import type { FC } from 'react'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'

const TocRoot = styled(AnimationDecorators)`
    height: 100%;
    overflow-y: auto;
`

const TocList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 4px 0;
`

const TocItem = styled.li<{ $level: 1 | 2 | 3; $isActive: boolean }>`
    padding: 4px 8px;
    padding-left: ${({ $level }) => ($level - 1) * 16 + 8}px;
    cursor: pointer;
    border-radius: 4px;
    font-size: ${({ $level }) => ($level === 1 ? '14px' : $level === 2 ? '13px' : '12px')};
    font-weight: ${({ $level }) => ($level === 1 ? 600 : $level === 2 ? 500 : 400)};
    line-height: 1.5;
    color: ${({ $isActive, theme }) => ($isActive ? 'var(--color-theme-7)' : 'var(--color-gray-700)')};
    background-color: ${({ $isActive }) => ($isActive ? 'var(--color-theme-1)' : 'transparent')};
    transition: background-color 0.15s, color 0.15s;
    word-break: break-word;

    &:hover {
        background-color: ${({ $isActive }) => ($isActive ? 'var(--color-theme-1)' : 'var(--color-gray-100)')};
    }
`

const EmptyPlaceholder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 48px;
    color: var(--color-gray-400);
    font-size: 13px;
`

export interface TocBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: TocBlockAbstract
    headings: TocHeading[]
    activeIndex?: number
    onHeadingClick: (heading: TocHeading) => void
}

const TocBlock: FC<TocBlockProps> = ({ blockData, headings, activeIndex, onHeadingClick, ...rest }) => {
    const { config, title, id } = blockData
    const { breakPoint, anchor, cursor } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)

    const { appId = '' } = useApplicationContext()

    const handleClick = useCallback(
        (heading: TocHeading) => (e: React.MouseEvent) => {
            e.stopPropagation()
            onHeadingClick(heading)
        },
        [onHeadingClick]
    )

    return (
        <TocRoot
            blockId={id}
            blockName={title}
            animation={breakPoint?.animation}
            id={effectiveAnchorId}
            style={getCursorStyle(appId, cursor)}
            {...rest}
        >
            {headings.length === 0 ? (
                <EmptyPlaceholder>暂无标题</EmptyPlaceholder>
            ) : (
                <TocList>
                    {headings.map(heading => (
                        <TocItem
                            key={heading.index}
                            $level={heading.level}
                            $isActive={activeIndex === heading.index}
                            onClick={handleClick(heading)}
                            title={heading.text}
                        >
                            {heading.text}
                        </TocItem>
                    ))}
                </TocList>
            )}
        </TocRoot>
    )
}

export default React.memo(TocBlock)
